import { sortBy } from "lodash/fp";

import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import useMetricsQuery, {
  MeetOpsQueryResult,
} from "@app/analytics/hooks/metrics/useMetricsQuery";
import { MeetingPolicyMetric } from "@app/analytics/MeetingPolicy/types";
import { multiParticipantMeeting } from "@app/analytics/meetops/filters";
import { MeetOpsQuery, QueryDimension } from "@app/analytics/meetops/types";
import { DateRange } from "@app/common/types";

import { getSeries } from "./meetingPolicyUtils";
import { getReadAvgData } from "./readAverages";
import useMeetingPolicy from "./useMeetingPolicy";

interface UseMeetingPolicyParams {
  dimension: QueryDimension;
}

const getDimensionRange = (
  dimension: QueryDimension,
  metric: MeetingPolicyMetric,
  data?: MeetOpsQueryResult
) => {
  const result = data?.results[0].data;
  if (!result || result.length === 0) {
    return [null, null];
  }

  const sortedResult = sortBy((item) => item[metric].value, result);
  return [
    sortedResult[0][dimension],
    sortedResult[sortedResult.length - 1][dimension],
  ];
};

const getQuery = (
  dateRange: DateRange | null,
  dimension: QueryDimension,
  peopleValue: "only_me" | "everyone",
  teamId?: string,
  userId?: string
): MeetOpsQuery | null => {
  if (dateRange === null) {
    return null;
  }

  return {
    dateRanges: [dateRange],
    dimensions: [dimension],
    metrics: [
      "engagement",
      "sentiment",
      "meetingScore",
      "onTimeRate",
      "meetingCount",
    ],
    filters: [multiParticipantMeeting()],
    people: peopleValue,
    userId,
    teamId,
  };
};

const useMeetingPolicyQuery = ({ dimension }: UseMeetingPolicyParams) => {
  const { filterBy, teamId, userId } = useRollupContext();
  const viewingIndividualMetrics =
    filterBy === "individual" || filterBy === "me";
  const peopleValue = viewingIndividualMetrics ? "only_me" : "everyone";

  const { startDate, endDate, selectedMetric } = useMeetingPolicy();

  const queryDateRange =
    startDate && endDate
      ? {
          startDate,
          endDate,
        }
      : null;

  const metricsQuery = useMetricsQuery(
    getQuery(queryDateRange, dimension, peopleValue, teamId, userId),
    { staleTime: 30 * 60 * 1000 }
  );

  const [lowest, highest] = getDimensionRange(
    dimension,
    selectedMetric,
    metricsQuery.data
  );

  return {
    highest,
    lowest,
    readSeries: getSeries(dimension, selectedMetric, getReadAvgData(dimension)),
    meetingCountSeries: getSeries(dimension, "meetingCount", metricsQuery.data),
    metricSeries: getSeries(dimension, selectedMetric, metricsQuery.data),
    ...metricsQuery,
  };
};

export default useMeetingPolicyQuery;
